import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Logo } from '../components/ui/Logo'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  ArrowLeft, FolderOpen, FolderClosed, Plus, Trash2, FileText, Database,
  Upload, RefreshCw, CheckCircle2, AlertCircle, X, Building2,
  ChevronRight, ChevronDown
} from 'lucide-react'
import { useAuth } from '../hooks/use-auth'
import { useI18n } from '../i18n'
import { get, post } from '../helper/network'
import API from '../helper/api'
import axios from 'axios'

interface Category {
  categoryId: string
  categoryName: string
  parentCategoryId?: string
}

interface FileItem {
  cloudFileId: string
  fileName: string
  filePath: string
  fileSize: number
  suffix: string
  categoryId: string
  indexId?: string
  status: number
  createTime: string
}

interface IndexItem {
  indexId: string
  indexName?: string
}

export default function KnowledgeBasePage() {
  const { isLoggedIn } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()

  const [categories, setCategories] = useState<Category[]>([])
  const [files, setFiles] = useState<FileItem[]>([])
  const [indexes, setIndexes] = useState<IndexItem[]>([])
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [loading, setLoading] = useState(false)

  // 展开的分类
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  // 新建分类
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryParent, setNewCategoryParent] = useState<string>('')
  const [creatingCategory, setCreatingCategory] = useState(false)

  // 上传文件
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 构建索引
  const [showBuildIndex, setShowBuildIndex] = useState(false)
  const [indexName, setIndexName] = useState('')
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  const [building, setBuilding] = useState(false)
  const [indexStatus, setIndexStatus] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return }
    loadData()
  }, [isLoggedIn])

  useEffect(() => {
    if (activeCategory !== undefined) loadFiles()
  }, [activeCategory])

  // 构建树结构
  const rootCategories = categories.filter((c) => !c.parentCategoryId)
  const getChildren = (parentId: string) => categories.filter((c) => c.parentCategoryId === parentId)

  const toggleExpand = (catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(catId)) next.delete(catId)
      else next.add(catId)
      return next
    })
  }

  const loadData = async () => {
    setLoading(true)
    await Promise.all([loadCategories(), loadFiles(), loadIndexes()])
    setLoading(false)
  }

  const loadCategories = async () => {
    try {
      const res: any = await post(API.POST_LIST_CATEGORY, {})
      if (res && res.code === 200 && res.data) {
        setCategories(res.data)
        // 默认展开所有有子分类的
        const parents = res.data.filter((c: Category) =>
          res.data.some((child: Category) => child.parentCategoryId === c.categoryId)
        )
        setExpandedCategories(new Set(parents.map((c: Category) => c.categoryId)))
      }
    } catch {}
  }

  const loadFiles = async () => {
    try {
      const params: any = {}
      if (activeCategory !== 'all') params.categoryId = activeCategory
      const res: any = await get(API.GET_FILE_STATUS, params)
      if (res && res.code === 200 && res.data) {
        setFiles(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
  }

  const loadIndexes = async () => {
    try {
      const res: any = await post(API.POST_INDEX_STATUS, {})
      if (res && res.code === 200 && res.data) {
        setIndexes(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
  }

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return
    setCreatingCategory(true)
    try {
      const params: any = { categoryName: newCategoryName.trim() }
      if (newCategoryParent) params.parentCategoryId = newCategoryParent
      const res: any = await post(API.POST_ADD_CATEGORY, params)
      if (res && res.code === 200) {
        setNewCategoryName('')
        setNewCategoryParent('')
        setShowNewCategory(false)
        loadCategories()
      }
    } catch {}
    setCreatingCategory(false)
  }

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const res: any = await post(API.POST_DELETE_CATEGORY, { categoryId })
      if (res && res.code === 200) {
        if (activeCategory === categoryId) setActiveCategory('all')
        loadCategories()
      }
    } catch {}
  }

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (activeCategory !== 'all') formData.append('categoryId', activeCategory)

      const res: any = await axios.post(API.GET_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        validateStatus: (status: number) => status >= 200 && status < 400,
      })

      if (res.data && res.data.code === 200) {
        loadFiles()
      }
    } catch {}
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDeleteFile = async (fileId: string) => {
    try {
      const res: any = await post(API.POST_DELETE_FILE, { fileId })
      if (res && res.code === 200) loadFiles()
    } catch {}
  }

  const handleBuildIndex = async () => {
    if (!indexName.trim() || selectedFileIds.length === 0) return
    setBuilding(true)
    setIndexStatus(null)
    try {
      const res: any = await post(API.POST_INDEX_INIT, {
        name: indexName.trim(),
        documentIds: selectedFileIds,
      })
      if (res && res.code === 200 && res.data) {
        setIndexStatus('running')
        setShowBuildIndex(false)
        setIndexName('')
        setSelectedFileIds([])
        pollIndexStatus(res.data.jobId || res.data.indexId, res.data.indexId)
      }
    } catch {}
    setBuilding(false)
  }

  const pollIndexStatus = async (jobId: string, indexId?: string) => {
    const poll = async () => {
      try {
        const res: any = await post(API.POST_INDEX_STATUS, { jobId, indexId })
        if (res && res.code === 200 && res.data) {
          const status = res.data.status || res.data.jobStatus
          if (status === 'RUNNING' || status === 'PENDING' || status === 'QUEUED') {
            setIndexStatus('running')
            setTimeout(poll, 3000)
          } else if (status === 'SUCCEEDED' || status === 'COMPLETED') {
            setIndexStatus('done')
            loadIndexes()
          } else {
            setIndexStatus('failed')
          }
        }
      } catch { setIndexStatus('failed') }
    }
    setTimeout(poll, 2000)
  }

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    )
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }

  const getCategoryPath = (catId: string): string => {
    const parts: string[] = []
    let current = categories.find((c) => c.categoryId === catId)
    while (current) {
      parts.unshift(current.categoryName)
      current = current.parentCategoryId
        ? categories.find((c) => c.categoryId === current!.parentCategoryId)
        : undefined
    }
    return parts.join(' / ')
  }

  // 递归渲染分类树
  const renderCategoryTree = (cats: Category[], depth: number = 0) => {
    return cats.map((cat) => {
      const children = getChildren(cat.categoryId)
      const hasChildren = children.length > 0
      const isExpanded = expandedCategories.has(cat.categoryId)
      const isActive = activeCategory === cat.categoryId

      return (
        <div key={cat.categoryId}>
          <div className="flex items-center group mb-0.5" style={{ paddingLeft: `${depth * 16}px` }}>
            {/* 展开/折叠按钮 */}
            {hasChildren ? (
              <button
                onClick={() => toggleExpand(cat.categoryId)}
                className="p-1 rounded text-gray-300 hover:text-gray-500 shrink-0"
              >
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}

            {/* 分类按钮 */}
            <button
              onClick={() => { setActiveCategory(cat.categoryId); if (hasChildren) toggleExpand(cat.categoryId) }}
              className={`flex-1 text-left px-2 py-1.5 rounded-xl text-sm transition-all flex items-center gap-1.5 ${
                isActive ? 'bg-orange-50 text-[#1a1a1a] font-medium' : 'text-gray-500 hover:bg-black/[0.03] hover:text-[#1a1a1a]'
              }`}
            >
              {isActive ? (
                <FolderOpen className="h-3.5 w-3.5 text-orange-500 shrink-0" />
              ) : (
                <FolderClosed className="h-3.5 w-3.5 opacity-40 shrink-0" />
              )}
              <span className="truncate">{cat.categoryName}</span>
              {hasChildren && <span className="text-[10px] text-gray-300 ml-auto shrink-0">{children.length}</span>}
            </button>

            {/* 操作按钮 */}
            <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => { setNewCategoryParent(cat.categoryId); setShowNewCategory(true) }}
                className="p-1 rounded text-gray-300 hover:text-orange-500 transition-colors"
                title={t('添加子分类', 'Add subcategory')}
              >
                <Plus className="h-3 w-3" />
              </button>
              <button
                onClick={() => handleDeleteCategory(cat.categoryId)}
                className="p-1 rounded text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* 子分类 */}
          {hasChildren && isExpanded && renderCategoryTree(children, depth + 1)}
        </div>
      )
    })
  }

  if (!isLoggedIn) return null

  const activeCategoryName = activeCategory === 'all'
    ? t('全部文件', 'All Files')
    : getCategoryPath(activeCategory)

  return (
    <div className="h-screen flex flex-col bg-[#fafafa]">
      {/* 顶栏 */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 shrink-0 z-30">
        <Link to="/workspace" className="flex items-center gap-2 text-lg font-semibold mr-2 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>
        <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all mr-4">
          <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
          {t('返回工作台', 'Back')}
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/workspace" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            {t('对话', 'Chat')}
          </Link>
          <button className="px-3 py-1.5 text-[13px] rounded-lg text-[#1a1a1a] font-medium bg-black/[0.04]">
            <Database className="h-3.5 w-3.5 inline mr-1.5" />
            {t('知识库', 'Knowledge')}
          </button>
          <Link to="/enterprise" className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            <Building2 className="h-3.5 w-3.5 inline mr-1.5" />
            {t('企业', 'Enterprise')}
          </Link>
        </nav>
        <div className="ml-auto" />
      </header>

      <div className="flex flex-1 min-h-0">
        {/* 左侧 - 分类树 */}
        <aside className="hidden md:flex w-72 flex-col border-r border-black/[0.04] bg-white/50">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('分类', 'Categories')}</span>
              <button
                onClick={() => { setShowNewCategory(!showNewCategory); setNewCategoryParent('') }}
                className="p-1 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50/50 transition-all"
                title={t('新建分类', 'New Category')}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {showNewCategory && (
              <div className="space-y-1.5 mb-2 p-2.5 rounded-xl bg-white border border-black/[0.04]">
                {newCategoryParent && (
                  <div className="flex items-center gap-1 text-[10px] text-gray-400">
                    <span>{t('父分类:', 'Parent:')}</span>
                    <span className="text-orange-500">{categories.find((c) => c.categoryId === newCategoryParent)?.categoryName}</span>
                    <button onClick={() => setNewCategoryParent('')} className="text-gray-300 hover:text-gray-500">&times;</button>
                  </div>
                )}
                <div className="flex gap-1.5">
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder={t('分类名称', 'Category name')}
                    className="h-8 text-xs rounded-lg bg-white/60 border-black/[0.06]"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                    autoFocus
                  />
                  <Button size="sm" className="h-8 px-2 bg-orange-500 hover:bg-orange-600 rounded-lg shrink-0" onClick={handleCreateCategory} disabled={creatingCategory}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-2">
            {/* 全部文件 */}
            <button
              onClick={() => setActiveCategory('all')}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all mb-0.5 ${
                activeCategory === 'all' ? 'bg-orange-50 text-[#1a1a1a] font-medium' : 'text-gray-500 hover:bg-black/[0.03] hover:text-[#1a1a1a]'
              }`}
            >
              <FolderOpen className="h-3.5 w-3.5 inline mr-2 opacity-40" />
              {t('全部文件', 'All Files')}
            </button>

            {/* 分类树 */}
            {renderCategoryTree(rootCategories)}
          </div>

          {/* 索引状态 */}
          {indexStatus && (
            <div className="p-3 border-t border-black/[0.04]">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${
                indexStatus === 'done' ? 'bg-emerald-50 text-emerald-600' :
                indexStatus === 'failed' ? 'bg-red-50 text-red-500' :
                'bg-orange-50 text-orange-600'
              }`}>
                {indexStatus === 'running' && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                {indexStatus === 'done' && <CheckCircle2 className="h-3.5 w-3.5" />}
                {indexStatus === 'failed' && <AlertCircle className="h-3.5 w-3.5" />}
                {indexStatus === 'running' ? t('索引构建中...', 'Building index...') :
                 indexStatus === 'done' ? t('索引构建完成', 'Index built') :
                 t('索引构建失败', 'Index failed')}
              </div>
            </div>
          )}
        </aside>

        {/* 主内容区 */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* 工具栏 */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-black/[0.04] bg-white/30">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-[#1a1a1a] text-white hover:bg-[#333] h-9 text-sm font-medium rounded-xl"
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? t('上传中...', 'Uploading...') : t('上传文件', 'Upload')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.csv"
                className="hidden"
                onChange={handleUploadFile}
                multiple
              />
            </div>

            {selectedFileIds.length > 0 && (
              <Button
                onClick={() => setShowBuildIndex(true)}
                variant="outline"
                className="border-orange-200 text-orange-600 hover:bg-orange-50 h-9 text-sm rounded-xl"
              >
                <Database className="h-4 w-4 mr-2" />
                {t('构建索引', 'Build Index')} ({selectedFileIds.length})
              </Button>
            )}

            <Button variant="ghost" size="sm" onClick={loadData} className="text-gray-400 hover:text-[#1a1a1a] h-9 px-2">
              <RefreshCw className="h-4 w-4" />
            </Button>

            <span className="text-xs text-gray-400 ml-auto">
              {activeCategory !== 'all' && <span className="text-orange-500 mr-2">{activeCategoryName}</span>}
              {files.length} {t('个文件', 'files')}
            </span>
          </div>

          {/* 构建索引弹窗 */}
          {showBuildIndex && (
            <div className="mx-6 mt-4 p-4 rounded-2xl bg-white border border-orange-100 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-[#1a1a1a]">{t('构建知识库索引', 'Build Knowledge Index')}</h3>
                <button onClick={() => setShowBuildIndex(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">{t('索引名称', 'Index Name')}</Label>
                  <Input
                    value={indexName}
                    onChange={(e) => setIndexName(e.target.value)}
                    placeholder={t('例如：公司规章制度库', 'e.g., Company Policies')}
                    className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  {t(`已选择 ${selectedFileIds.length} 个文件`, `${selectedFileIds.length} files selected`)}
                </p>
                <Button
                  onClick={handleBuildIndex}
                  className="bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm rounded-xl"
                  disabled={building || !indexName.trim()}
                >
                  {building ? t('构建中...', 'Building...') : t('开始构建', 'Start Build')}
                </Button>
              </div>
            </div>
          )}

          {/* 文件列表 */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {files.length > 0 ? (
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.cloudFileId}
                    className={`flex items-center gap-3 p-3 rounded-2xl bg-white border transition-all group ${
                      selectedFileIds.includes(file.cloudFileId) ? 'border-orange-200 bg-orange-50/30' : 'border-black/[0.04] hover:border-orange-100'
                    }`}
                  >
                    <button
                      onClick={() => toggleFileSelection(file.cloudFileId)}
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                        selectedFileIds.includes(file.cloudFileId) ? 'bg-orange-500 border-orange-500' : 'border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      {selectedFileIds.includes(file.cloudFileId) && <CheckCircle2 className="h-3 w-3 text-white" />}
                    </button>

                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-blue-500" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1a1a1a] truncate">{file.fileName}</p>
                      <p className="text-[11px] text-gray-400">
                        {formatFileSize(file.fileSize)} · {file.suffix?.toUpperCase()}
                        {file.indexId && <span className="ml-2 text-emerald-500">{t('已索引', 'Indexed')}</span>}
                      </p>
                    </div>

                    <button
                      onClick={() => handleDeleteFile(file.cloudFileId)}
                      className="p-2 rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-blue-400" />
                </div>
                <h2 className="text-lg font-semibold text-[#1a1a1a] mb-1">{t('知识库为空', 'Knowledge Base is Empty')}</h2>
                <p className="text-sm text-gray-400 mb-6 text-center max-w-sm">
                  {activeCategory !== 'all'
                    ? t('当前分类下暂无文件，点击上传', 'No files in this category. Upload one.')
                    : t('上传企业文档，构建专属知识库，让AI管家深度理解你的业务', 'Upload documents to build a knowledge base for your AI butler')
                  }
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-[#1a1a1a] text-white hover:bg-[#333] h-10 text-sm font-medium rounded-xl"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t('上传文件', 'Upload File')}
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
